import api from './api';
import { ApiEndpoints } from './ApiUrls';

// Types for RFID Reader
export interface ReaderType {
  id: string;
  code: string;
  name: string;
  description: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface Reader {
  id: string;
  name: string;
  device_id: string;
  description: string | null;
  scan_interval: number;
  debounce_time: number;
  threshold_time: number;
  location: string | null;
  location_detail?: {
    id: string;
    name: string;
    location_type?: string;
  } | null;
  reader_type: string;
  reader_type_display?: string;
  company: string;
  created_at: string;
  updated_at: string;
  created_by?: string | null;
  updated_by?: string | null;
  last_seen?: string | null;
  mqtt_config?: {
    hostname: string;
    port: number;
    client_id: string;
    username: string;
    password: string;
    publish_topic: string;
    subscription_topic: string;
    keep_alive: number;
  } | null;
}

export type ReaderPayload = Pick<
  Reader,
  'name' | 'device_id' | 'description' | 'scan_interval' | 'debounce_time' |
  'threshold_time' | 'location' | 'reader_type'
>;

// Types for Vehicle
export interface Vehicle {
  id: string;
  name: string;
  registration_number: string;
  manufacturer: string | null;
  manufacturer_detail?: { id: string; name: string } | null;
  model: string | null;
  model_detail?: { id: string; name: string; manufacturer_id?: string } | null;
  description: string | null;
  epc_id: string | null;
  company: string;
  company_detail?: string | null;
  created_at: string;
  updated_at: string;
  created_by: string | null;
  updated_by: string | null;
}

// Types for Vehicle Transfer Matching Config
export interface VehicleTransferMatchingConfig {
  id: string;
  name: string;
  description: string | null;
  is_active: boolean;
  entry_action: 'NONE' | 'AUTO_CHECKOUT' | 'AUTO_CHECKIN' | 'NOTIFY_ONLY';
  entry_action_display?: string;
  exit_action: 'NONE' | 'AUTO_CHECKOUT' | 'AUTO_CHECKIN' | 'NOTIFY_ONLY';
  exit_action_display?: string;
  validate_route: boolean;
  notify_on_entry: boolean;
  notify_on_exit: boolean;
  notify_on_route_violation: boolean;
  company: string;
  company_name?: string;
  created_at: string;
  updated_at: string;
  created_by: string | null;
  created_by_name?: string;
  updated_by: string | null;
  updated_by_name?: string;
}

// Types for Vehicle Availability
export interface VehicleAvailability {
  vehicle_id: string;
  available: boolean;
  active_transfer: {
    transfer_id: string;
    status: string;
  } | null;
}

// Types for Reader Auth Attempt
export interface ReaderAuthAttempt {
  id: string;
  reader: string | null;
  device_id: string;
  ip_address: string;
  timestamp: string;
  success: boolean;
  failure_reason: string | null;
}

// Types for Asset Scan
export interface AssetScan {
  id: string;
  reader: string;
  asset: string;
  epc_id: string;
  timestamp: string;
  is_vehicle: boolean;
  vehicle_type: string | null;
}

// Types for Reader Activity Log
export interface ReaderActivityLog {
  id: string;
  reader: string;
  activity_type: 'CONNECT' | 'DISCONNECT' | 'AUTH_SUCCESS' | 'AUTH_FAILURE' | 'DATA_RECEIVED' | 'ERROR';
  timestamp: string;
  details: any | null;
}

// Types for Location Hierarchy
export interface LocationNode {
  id: string;
  name: string;
  children: LocationNode[];
}

// Types for Reader Scan Statistics
export interface ReaderScanStatistics {
  total_scans: number;
  unique_assets: number;
  scans_by_date: Record<string, number>;
  scans_by_hour: Record<string, number>;
  asset_types: Record<string, number>;
}

// Reader Authentication and Security
export const authenticateReader = async (deviceId: string, authKey: string) => {
  const response = await api.post(ApiEndpoints.rfid.authenticate, {
    device_id: deviceId,
    auth_key: authKey
  });
  return response.data;
};

export const generateReaderKey = async (readerId: string, expiresInDays?: number) => {
  const response = await api.post(ApiEndpoints.rfid.generateKey(readerId), {
    expires_in_days: expiresInDays
  });
  return response.data;
};

export const revokeReaderKey = async (readerId: string, reason?: string) => {
  const response = await api.post(ApiEndpoints.rfid.revokeKey(readerId), {
    reason: reason
  });
  return response.data;
};

export const getReaderAuthAttempts = async (params?: any) => {
  const response = await api.get(ApiEndpoints.rfid.authAttempts, { params });
  return response.data;
};

export const getReaderSpecificAuthAttempts = async (readerId: string, params?: any) => {
  const response = await api.get(ApiEndpoints.rfid.readerAuthAttempts(readerId), { params });
  return response.data;
};

// Reader Type Management
export const getReaderTypes = async (params?: any) => {
  const response = await api.get(ApiEndpoints.rfid.readerTypes, { params });
  return response.data;
};

export const getReaderTypeById = async (typeId: string) => {
  const response = await api.get(ApiEndpoints.rfid.readerTypeDetail(typeId));
  return response.data;
};

export const getActiveReaderTypes = async () => {
  const response = await api.get(ApiEndpoints.rfid.activeReaderTypes);
  return response.data;
};

export const createReaderType = async (typeData: Partial<ReaderType>) => {
  const response = await api.post(ApiEndpoints.rfid.readerTypes, typeData);
  return response.data;
};

export const updateReaderType = async (typeId: string, typeData: Partial<ReaderType>) => {
  const response = await api.put(ApiEndpoints.rfid.readerTypeDetail(typeId), typeData);
  return response.data;
};

export const deleteReaderType = async (typeId: string) => {
  const response = await api.delete(ApiEndpoints.rfid.readerTypeDetail(typeId));
  return response.data;
};

// Reader Management
export const getReaders = async (params?: any) => {
  const response = await api.get(ApiEndpoints.rfid.readers, { params });
  return response.data;
};

export const getReaderById = async (readerId: string) => {
  const response = await api.get(ApiEndpoints.rfid.readerDetail(readerId));
  return response.data;
};

export const createReader = async (readerData: Partial<ReaderPayload>) => {
  const response = await api.post(ApiEndpoints.rfid.readers, readerData);
  return response.data;
};

export const updateReader = async (readerId: string, readerData: Partial<ReaderPayload>) => {
  const response = await api.put(ApiEndpoints.rfid.readerDetail(readerId), readerData);
  return response.data;
};

export const deleteReader = async (readerId: string) => {
  const response = await api.delete(ApiEndpoints.rfid.readerDetail(readerId));
  return response.data;
};

// Revokes any existing MQTT broker credential for this reader and provisions a
// fresh one (new random username, client_id, and password). Returns the
// updated Reader, including its refreshed mqtt_config.
export const generateReaderMqttCredentials = async (readerId: string): Promise<Reader> => {
  const response = await api.post(ApiEndpoints.rfid.readerMqttCredentials(readerId));
  return response.data;
};

export const getReaderActivityLogs = async (readerId: string, params?: any) => {
  const response = await api.get(ApiEndpoints.rfid.readerActivityLogs(readerId), { params });
  return response.data;
};

export const bulkImportReaders = async (formData: FormData) => {
  const response = await api.post(ApiEndpoints.rfid.bulkImport, formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
  return response.data;
};

// Reader Location Assignment
export const getLocationHierarchy = async () => {
  const response = await api.get(ApiEndpoints.rfid.locationHierarchy);
  return response.data;
};

export const getReadersByLocation = async (locationId: string, params?: any) => {
  const response = await api.get(ApiEndpoints.rfid.locationReaders(locationId), { params });
  return response.data;
};

// Reader-based Reporting
export const getAssetScans = async (params?: any) => {
  const response = await api.get(ApiEndpoints.rfid.assetScans, { params });
  return response.data;
};

export const getReaderScanStatistics = async (readerId: string, params?: any) => {
  const response = await api.get(ApiEndpoints.rfid.readerScanStatistics(readerId), { params });
  return response.data;
};

export const exportAssetScans = async (params?: any) => {
  const response = await api.get(ApiEndpoints.rfid.assetScanExport, { 
    params,
    responseType: 'blob'
  });
  return response.data;
};

// Vehicle Management
export const getVehicles = async (params?: any) => {
  const response = await api.get(ApiEndpoints.rfid.vehicles, { params });
  return response.data;
};

// Vehicle dropdown listing (supports exclude_engaged=true to drop vehicles
// already tied up in an open transfer or movement-tracked activity).
export const getVehicleDropdown = async (params?: any) => {
  const response = await api.get(ApiEndpoints.rfid.vehicleDropdown, { params });
  return response.data;
};

export const getVehicleById = async (vehicleId: string) => {
  const response = await api.get(ApiEndpoints.rfid.vehicleDetail(vehicleId));
  return response.data;
};

export const createVehicle = async (vehicleData: Partial<Vehicle>) => {
  const response = await api.post(ApiEndpoints.rfid.vehicles, vehicleData);
  return response.data;
};

export const updateVehicle = async (vehicleId: string, vehicleData: Partial<Vehicle>) => {
  const response = await api.put(ApiEndpoints.rfid.vehicleDetail(vehicleId), vehicleData);
  return response.data;
};

export const deleteVehicle = async (vehicleId: string) => {
  const response = await api.delete(ApiEndpoints.rfid.vehicleDetail(vehicleId));
  return response.data;
};

export const patchVehicle = async (vehicleId: string, vehicleData: Partial<Vehicle>) => {
  const response = await api.patch(ApiEndpoints.rfid.vehicleDetail(vehicleId), vehicleData);
  return response.data;
};

// Vehicle Scan Operations
export const getAllVehicleScans = async (params?: any) => {
  const response = await api.get(ApiEndpoints.rfid.vehicleScanAll, { params });
  return response.data;
};

export const scanVehicleWithHandheld = async (epcId: string, readerId: string) => {
  const response = await api.post(ApiEndpoints.rfid.vehicleScanHandheld, { epc_id: epcId, reader_id: readerId });
  return response.data;
};

export const processFixedReaderScan = async (readerId: string, epcIds: string[], scanTimestamp?: string) => {
  const response = await api.post(ApiEndpoints.rfid.readerVehicleScan(readerId), {
    epc_ids: epcIds,
    scan_timestamp: scanTimestamp
  });
  return response.data;
};

// Vehicle Transfer Integration
export const checkVehicleAvailability = async (vehicleId: string) => {
  const response = await api.get(ApiEndpoints.rfid.vehicleAvailability(vehicleId));
  return response.data;
};

// Vehicle Transfer Matching Configuration
export const getTransferMatchingConfigs = async (params?: any) => {
  const response = await api.get(ApiEndpoints.rfid.transferMatchingConfigs, { params });
  return response.data;
};

export const getTransferMatchingConfigById = async (configId: string) => {
  const response = await api.get(ApiEndpoints.rfid.transferMatchingConfigDetail(configId));
  return response.data;
};

export const createTransferMatchingConfig = async (configData: Partial<VehicleTransferMatchingConfig>) => {
  const response = await api.post(ApiEndpoints.rfid.transferMatchingConfigs, configData);
  return response.data;
};

export const updateTransferMatchingConfig = async (configId: string, configData: Partial<VehicleTransferMatchingConfig>) => {
  const response = await api.put(ApiEndpoints.rfid.transferMatchingConfigDetail(configId), configData);
  return response.data;
};

export const deleteTransferMatchingConfig = async (configId: string) => {
  const response = await api.delete(ApiEndpoints.rfid.transferMatchingConfigDetail(configId));
  return response.data;
};

export const getActiveTransferMatchingConfigs = async () => {
  const response = await api.get(ApiEndpoints.rfid.activeTransferMatchingConfigs);
  return response.data;
};

export const toggleTransferMatchingConfig = async (configId: string) => {
  const response = await api.post(ApiEndpoints.rfid.toggleTransferMatchingConfig(configId));
  return response.data;
};

// Create service object
export const rfidService = {
  // Reader Authentication and Security
  authenticateReader,
  generateReaderKey,
  revokeReaderKey,
  getReaderAuthAttempts,
  getReaderSpecificAuthAttempts,
  
  // Reader Type Management
  getReaderTypes,
  getReaderTypeById,
  getActiveReaderTypes,
  createReaderType,
  updateReaderType,
  deleteReaderType,
  
  // Reader Management
  getReaders,
  getReaderById,
  createReader,
  updateReader,
  deleteReader,
  generateReaderMqttCredentials,
  getReaderActivityLogs,
  bulkImportReaders,
  
  // Reader Location Assignment
  getLocationHierarchy,
  getReadersByLocation,
  
  // Reader-based Reporting
  getAssetScans,
  getReaderScanStatistics,
  exportAssetScans,
  
  // Vehicle Management
  getVehicles,
  getVehicleDropdown,
  getVehicleById,
  createVehicle,
  updateVehicle,
  deleteVehicle,
  patchVehicle,
  
  // Vehicle Scan Operations
  getAllVehicleScans,
  scanVehicleWithHandheld,
  processFixedReaderScan,
  
  // Vehicle Transfer Integration
  checkVehicleAvailability,
  
  // Vehicle Transfer Matching Configuration
  getTransferMatchingConfigs,
  getTransferMatchingConfigById,
  createTransferMatchingConfig,
  updateTransferMatchingConfig,
  deleteTransferMatchingConfig,
  getActiveTransferMatchingConfigs,
  toggleTransferMatchingConfig,
};

export default rfidService;
